/**
 * Subscription and Business Logic Utilities
 *
 * Centralized business logic for subscription validation, trial management,
 * and user redirection logic. Used across authentication flows and organization switching.
 *
 * @module subscriptionUtils
 */

import { Paths } from './index'

/**
 * Validates if an organization has a valid subscription, trial, or free plan.
 *
 * Business rules:
 * - Active subscription is always valid
 * - Trial is valid if not expired
 * - Free plan is always valid
 *
 * @param {Object} organisation - Organization object from API
 * @param {boolean} organisation.hasActiveSubscription - Whether org has paid subscription
 * @param {Object} organisation.trial - Trial information
 * @param {boolean} organisation.trial.hasExpired - Whether trial has expired
 * @param {boolean} organisation.freePlan - Whether org is on free plan
 * @returns {boolean} True if organization has valid access
 *
 * @example
 * // Active subscription
 * hasValidSubscription({ hasActiveSubscription: true }) // → true
 *
 * // Valid trial
 * hasValidSubscription({ trial: { hasExpired: false } }) // → true
 *
 * // Expired trial, no subscription
 * hasValidSubscription({ trial: { hasExpired: true } }) // → false
 *
 * // Free plan
 * hasValidSubscription({ freePlan: true }) // → true
 */
export const hasValidSubscription = (organisation) => {
    if (!organisation) {
        return false
    }

    // Active subscription is always valid
    if (organisation.hasActiveSubscription) {
        return true
    }

    // Trial is valid if not expired
    if (organisation.trial && !organisation.trial.hasExpired) {
        return true
    }

    // Free plan is always valid
    if (organisation.freePlan) {
        return true
    }

    return false
}

/**
 * Determines the appropriate redirect path based on organization subscription status.
 *
 * Redirect logic:
 * - Invalid subscription → NO_ACTIVE_SUBSCRIPTION_PATHNAME
 * - Root path (/) → DASHBOARD_PATHNAME
 * - Other paths → null (stay on current path)
 *
 * @param {Object} organisation - Organization object from API
 * @param {string} [currentPath] - Current pathname from location
 * @returns {string|null} Path to redirect to, or null if no redirect needed
 *
 * @example
 * // Expired subscription from any page
 * determineRedirectPath({ trial: { hasExpired: true } }, '/quotes')
 * // → '/no-active-subscription'
 *
 * // Valid subscription from root
 * determineRedirectPath({ hasActiveSubscription: true }, '/')
 * // → '/dashboard'
 *
 * // Valid subscription from specific page
 * determineRedirectPath({ hasActiveSubscription: true }, '/quotes')
 * // → null (stay on current page)
 */
export const determineRedirectPath = (organisation, currentPath = null) => {
    // If no valid subscription, always redirect to subscription page
    if (!hasValidSubscription(organisation)) {
        return Paths.NO_ACTIVE_SUBSCRIPTION_PATHNAME
    }

    // If on root path, redirect to dashboard
    if (currentPath === '/') {
        return Paths.DASHBOARD_PATHNAME
    }

    // Otherwise, stay on current path
    return null
}

/**
 * Validates organization access and throws descriptive errors if invalid.
 *
 * @param {Object} organisation - Organization object to validate
 * @throws {Error} If organization is invalid or missing required fields
 *
 * @example
 * validateOrganisationAccess({ organisationId: '123' }) // ✅ passes
 * validateOrganisationAccess(null) // ❌ throws error
 */
export const validateOrganisationAccess = (organisation) => {
    if (!organisation) {
        throw new Error('Organisation is required but was not provided')
    }

    if (!organisation.organisationId) {
        throw new Error('Organisation ID is required but was missing')
    }

    return true
}

/**
 * Gets a human-readable description of the organization's subscription status.
 * Useful for logging, analytics, and debugging.
 *
 * @param {Object} organisation - Organization object
 * @returns {string} Human-readable status description
 *
 * @example
 * getSubscriptionStatus({ hasActiveSubscription: true })
 * // → 'active_subscription'
 *
 * getSubscriptionStatus({ trial: { hasExpired: false } })
 * // → 'active_trial'
 *
 * getSubscriptionStatus({ freePlan: true })
 * // → 'free_plan'
 */
export const getSubscriptionStatus = (organisation) => {
    if (!organisation) {
        return 'no_organisation'
    }

    if (organisation.hasActiveSubscription) {
        return 'active_subscription'
    }

    if (organisation.trial) {
        return organisation.trial.hasExpired ? 'expired_trial' : 'active_trial'
    }

    if (organisation.freePlan) {
        return 'free_plan'
    }

    return 'no_subscription'
}

/**
 * True when any subscription item is the Advanced plan product.
 *
 * Advanced already includes every trial feature, so trial-ending messaging is noise for these
 * vendors (QM-3471). Only meaningful alongside `organisation.hasActiveSubscription`.
 *
 * @param {Object} organisation - Organization object from API
 * @returns {boolean} True if the organisation is subscribed to the Advanced plan
 *
 * @example
 * isOnAdvancedPlan({ subscriptions: [{ subscriptionItems: [{ productId: 'prod_TERd29HwMGrlnr' }] }] }) // → true
 * isOnAdvancedPlan(null) // → false
 */
export const isOnAdvancedPlan = (organisation) => {
    const advancedProductId = import.meta.env.VITE_ADVANCED_PRODUCT_ID
    if (!advancedProductId) return false

    return (organisation?.subscriptions ?? []).some((subscription) =>
        (subscription?.subscriptionItems ?? []).some(
            (item) => item?.productId === advancedProductId || item?.product?.id === advancedProductId
        )
    )
}
